/* eslint-disable react/no-multi-comp */
import React from 'react';

import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';

import { buildAccommodationCancellationPolicies, formatDate } from '@luxuryescapes/lib-refunds';

import { Rate } from '~/services/AccommodationService';

interface CancellationPolicyModalProps {
  rate: Rate;
  currency: string;
  timezone: string;
}

export function FieldAndValue({ field, value }: { field: string; value: any }) {
  return (
    <Typography>
      <Typography component="span" fontWeight="bold">
        {field}:
      </Typography>{' '}
      {value}
    </Typography>
  );
}

export default function CancellationPolicyModal({ rate, currency, timezone }: CancellationPolicyModalProps) {
  const theme = useTheme();

  const hasCancellationPolicies = rate.cancellationPolicies && rate.cancellationPolicies.length > 0;

  const builtPolicies = buildAccommodationCancellationPolicies(rate, {
    currencyCode: currency,
    timezone,
  });

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        Timezone: {timezone}
      </Typography>

      {rate.fullRefundableUntil && (
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.success.main }}>
          Full refund available until {formatDate.formattedDateByTimezoneSimple(rate.fullRefundableUntil, { timezone })}
        </Typography>
      )}

      {rate.partiallyRefundableUntil && (
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.warning.main }}>
          Partial refund available until{' '}
          {formatDate.formattedDateByTimezoneSimple(rate.partiallyRefundableUntil, { timezone })}
        </Typography>
      )}

      {!rate.fullRefundableUntil && !rate.partiallyRefundableUntil && (
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.error.main }}>
          This rate is non-refundable. No cancellations or changes are allowed.
        </Typography>
      )}

      {builtPolicies.description.length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Cancellation Penalties (shown to the user):
          </Typography>
          {builtPolicies.description.map((policy, index: number) => (
            <Box key={index} sx={{ mb: 1, p: 1, bgcolor: theme.palette.grey[200], borderRadius: 1 }}>
              <Typography>
                <Typography component="span">{policy}</Typography>
              </Typography>
            </Box>
          ))}
        </>
      )}

      {hasCancellationPolicies && (
        <>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Cancellation Penalties (raw):
          </Typography>

          <Card>
            {rate.cancellationPolicies.map((policy) => (
              <CardContent key={policy.start} sx={{ mb: 1, p: 1, bgcolor: theme.palette.grey[200], borderRadius: 1 }}>
                <FieldAndValue field="Start" value={policy.start} />
                <FieldAndValue field="End" value={policy.end} />
                <FieldAndValue field="Amount" value={policy.amount} />
                <FieldAndValue field="Percent" value={policy.percent} />
                <FieldAndValue field="Policy Code" value={policy.policyCode} />
                <FieldAndValue field="Description" value={policy.description} />
                <FieldAndValue field="Non-refundable" value={policy.nonRefundable ? 'Yes' : 'No'} />
              </CardContent>
            ))}
          </Card>
        </>
      )}
    </Box>
  );
}
